import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  DataRoomFastDocument,
  DataRoomFastQuery,
  DataRoomFastQueryVariables,
  DataRoomFolderBrowseDocument,
  DataRoomFolderBrowseQuery,
  DataRoomFolderBrowseQueryVariables,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";

const STALE_TIME = 60_000;

export function usePrefetchDataRoom(dealId: string, delayMs = 150) {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => clear, []);

  const prefetch = () => {
    if (!dealId) {
      return;
    }

    void queryClient
      .prefetchQuery({
        queryKey: ["DataRoomFast", { dealId }],
        queryFn: () =>
          client.request<DataRoomFastQuery, DataRoomFastQueryVariables>(
            DataRoomFastDocument,
            { dealId },
          ),
        staleTime: STALE_TIME,
      })
      .then(() => {
        const cached = queryClient.getQueryData<DataRoomFastQuery>([
          "DataRoomFast",
          { dealId },
        ]);
        const folderId = cached?.deal.dataRoom.folder.id;
        if (!folderId) {
          return;
        }

        return queryClient.prefetchQuery({
          queryKey: ["DataRoomFolderBrowse", { id: folderId }],
          queryFn: () =>
            client.request<
              DataRoomFolderBrowseQuery,
              DataRoomFolderBrowseQueryVariables
            >(DataRoomFolderBrowseDocument, { id: folderId }),
          staleTime: STALE_TIME,
        });
      });
  };

  return {
    onMouseEnter: () => {
      clear();
      timerRef.current = setTimeout(prefetch, delayMs);
    },
    onMouseLeave: clear,
    onFocus: prefetch,
  };
}
